/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import React from 'react';

type TabKey = string;

type BlockNamedElement = React.ReactElement<{ blockName?: string }>;

export interface TabBlockProps {
  tabKey: TabKey;
  label?: string;
  children: React.ReactNode;
}

interface RootProps {
  children: React.ReactElement<TabBlockProps>[] | React.ReactElement<TabBlockProps>;
  keepMounted?: boolean;
  initialKey?: TabKey;
  initialKeys?: TabKey[];
  selectionMode?: 'single' | 'multiple';
  requireAtLeastOne?: boolean;
  onLastTabDeselectAttempt?: (key: TabKey) => void;
  classNames?: {
    root?: string;
    tabList?: string;
    tabButton?: string;
    tabButtonActive?: string;
    tabPanel?: string;
  };
}

function collectBlocks(node: React.ReactNode): React.ReactElement<TabBlockProps>[] {
  const out: React.ReactElement<TabBlockProps>[] = [];
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement(child)) return;
    if ((child.type as any) === Tabbed.Block) {
      out.push(child as React.ReactElement<TabBlockProps>);
    } else if ((child as any).props?.children) {
      out.push(...collectBlocks((child as any).props.children));
    }
  });
  return out;
}

function isBlockNamedElement(node: unknown): node is BlockNamedElement {
  return React.isValidElement(node) && typeof (node as any).props?.blockName === 'string';
}

function inferLabelFromChildren(children: React.ReactNode, fallback: string): string {
  const kids = React.Children.toArray(children);
  const env = kids.find(isBlockNamedElement);
  return env?.props.blockName ?? fallback;
}

const ALL_KEY = 'all';

export const Tabbed = {
  Root: function Root({
    children,
    keepMounted = true,
    initialKey,
    initialKeys,
    selectionMode = 'single',
    requireAtLeastOne = true,
    onLastTabDeselectAttempt,
    classNames,
  }: RootProps) {
    const blocks = React.useMemo(() => collectBlocks(children), [children]);
    const hasAll = blocks.some((b) => b.props.tabKey === ALL_KEY);
    const contentBlocks = React.useMemo(
      () => blocks.filter((b) => b.props.tabKey !== ALL_KEY),
      [blocks]
    );

    const defaultKey: string | undefined = hasAll ? ALL_KEY : contentBlocks[0]?.props.tabKey;
    const isMultiple = selectionMode === 'multiple';

    const initialOpen: string[] = React.useMemo(() => {
      if (isMultiple) {
        if (initialKeys && initialKeys.length) return initialKeys;
        if (initialKey) return [initialKey];
        return defaultKey ? [defaultKey] : [];
      } else {
        if (initialKey) return [initialKey];
        return defaultKey ? [defaultKey] : [];
      }
    }, [isMultiple, initialKey, initialKeys, defaultKey]);

    const [activeKey, setActiveKey] = React.useState<string>(initialOpen[0] ?? '');
    const [activeKeys, setActiveKeys] = React.useState<Set<string>>(
      () => new Set(initialOpen)
    );
    const [openOrder, setOpenOrder] = React.useState<string[]>(initialOpen);

    React.useEffect(() => {
      if (!requireAtLeastOne) return;
      if (isMultiple) {
        if (activeKeys.size === 0 && defaultKey) {
          setActiveKeys(new Set([defaultKey]));
          setActiveKey(defaultKey);
          setOpenOrder([defaultKey]);
        }
      } else {
        if (!activeKey && defaultKey) setActiveKey(defaultKey);
      }
    }, [requireAtLeastOne, isMultiple, activeKeys.size, activeKey, defaultKey]);

    const isOpen = (key: string) => (isMultiple ? activeKeys.has(key) : activeKey === key);

    const openOnly = (key: string) => {
      setActiveKey(key);
      setActiveKeys(new Set([key]));
      setOpenOrder([key]);
    };

    const toggleKey = (key: string) => {
      if (!isMultiple) {
        openOnly(key);
        return;
      }

      setActiveKeys((prev) => {
        if (key === ALL_KEY) {
          if (prev.has(ALL_KEY)) {
            if (requireAtLeastOne && prev.size === 1) {
              onLastTabDeselectAttempt?.(key);
              return prev;
            }
            const next = new Set(prev);
            next.delete(ALL_KEY);
            setOpenOrder((o) => o.filter((k) => k !== ALL_KEY));
            const first = next.values().next().value as string | undefined;
            setActiveKey(first ?? '');
            return next;
          } else {
            setActiveKey(ALL_KEY);
            setOpenOrder([ALL_KEY]);
            return new Set([ALL_KEY]);
          }
        }

        const next = new Set(prev);
        next.delete(ALL_KEY);
        setOpenOrder((o) => o.filter((k) => k !== ALL_KEY));

        if (next.has(key)) {
          if (requireAtLeastOne && next.size === 1) {
            onLastTabDeselectAttempt?.(key);
            return next;
          }
          next.delete(key);
          setOpenOrder((o) => o.filter((k) => k !== key));
        } else {
          next.add(key);
          setOpenOrder((o) => [...o.filter((k) => k !== key), key]);
        }

        if (!activeKey || !next.has(activeKey)) {
          const first = next.values().next().value as string | undefined;
          setActiveKey(first ?? '');
        }
        return next;
      });
    };

    const tabRefs = React.useRef<HTMLButtonElement[]>([]);
    const onKeyDown = (e: React.KeyboardEvent) => {
      const idx = tabRefs.current.findIndex((el) => el === document.activeElement);
      const list = blocks;
      const last = list.length - 1;
      if (idx < 0) return;

      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        const nextIdx = (idx + dir + list.length) % list.length;
        tabRefs.current[nextIdx]?.focus();
        if (!isMultiple) openOnly(list[nextIdx].props.tabKey);
      }
      if (e.key === 'Home') {
        e.preventDefault();
        tabRefs.current[0]?.focus();
        if (!isMultiple) openOnly(list[0].props.tabKey);
      }
      if (e.key === 'End') {
        e.preventDefault();
        tabRefs.current[last]?.focus();
        if (!isMultiple) openOnly(list[last].props.tabKey);
      }
      if (isMultiple && (e.key === ' ' || e.key === 'Enter')) {
        e.preventDefault();
        const key = list[idx].props.tabKey;
        toggleKey(key);
      }
    };

    const normalizedBlocks = React.useMemo(() => {
      return blocks.map((b, i) => {
        const label = b.props.label ?? inferLabelFromChildren(b.props.children, `Section ${i + 1}`);
        return { el: b, key: b.props.tabKey, label };
      });
    }, [blocks]);

    const blockByKey = React.useMemo(() => {
      const m = new Map<string, React.ReactElement<TabBlockProps>>();
      contentBlocks.forEach((b) => m.set(b.props.tabKey, b));
      return m;
    }, [contentBlocks]);

    const allOpen = isOpen(ALL_KEY);

    return (
      <div className={classNames?.root}>
        <div
          role={isMultiple ? 'toolbar' : 'tablist'}
          aria-label="Sections"
          className={classNames?.tabList}
          onKeyDown={onKeyDown}
        >
          {normalizedBlocks.map(({ key, label }, i) => {
            const active = isOpen(key);
            return (
              <button
                key={key}
                ref={(node) => {
                  if (node) tabRefs.current[i] = node;
                }}
                type="button"
                role={isMultiple ? undefined : 'tab'}
                aria-selected={isMultiple ? undefined : active}
                aria-pressed={isMultiple ? active : undefined}
                aria-controls={`${key}-panel`}
                id={`${key}-tab`}
                className={active ? classNames?.tabButtonActive : classNames?.tabButton}
                onClick={() => toggleKey(key)}
                title={label}
              >
                {label}
              </button>
            );
          })}
        </div>

        {hasAll && allOpen ? (
          <div
            key={ALL_KEY}
            id={`${ALL_KEY}-panel`}
            aria-labelledby={`${ALL_KEY}-tab`}
            className={classNames?.tabPanel}
            role={isMultiple ? 'region' : 'tabpanel'}
          >
            {contentBlocks.map((b) => b)}
          </div>
        ) : (
          <>
            {(() => {
              const selectedKeys = isMultiple
                ? openOrder.filter((k) => k !== ALL_KEY && activeKeys.has(k))
                : activeKey
                ? [activeKey]
                : [];
              const selectedSet = new Set(selectedKeys);

              return (
                <>
                  {selectedKeys.map((k) => {
                    const block = blockByKey.get(k);
                    if (!block) return null;
                    return (
                      <div
                        key={k}
                        id={`${k}-panel`}
                        aria-labelledby={`${k}-tab`}
                        className={classNames?.tabPanel}
                        role={isMultiple ? 'region' : 'tabpanel'}
                      >
                        {block}
                      </div>
                    );
                  })}

                  {contentBlocks
                    .filter((b) => !selectedSet.has(b.props.tabKey))
                    .map((b) =>
                      keepMounted ? (
                        <div
                          key={b.props.tabKey}
                          id={`${b.props.tabKey}-panel`}
                          aria-labelledby={`${b.props.tabKey}-tab`}
                          className={classNames?.tabPanel}
                          role={isMultiple ? 'region' : 'tabpanel'}
                          hidden
                        >
                          {b}
                        </div>
                      ) : null
                    )}
                </>
              );
            })()}
          </>
        )}
      </div>
    );
  },

  Block: function Block({ children }: TabBlockProps) {
    return <>{children}</>;
  },
};

export default Tabbed;
